import { conditionRefs, evaluateCondition } from "./conditions.js";
export function flatten(form) {
    const out = [];
    form.steps.forEach((step, stepIndex) => step.nodes.forEach((node) => out.push({ stepId: step.id, stepTitle: step.title, stepIndex, index: out.length, node })));
    return out;
}
const answered = (answers, id) => {
    const v = answers[id];
    return !(v === undefined || v === "" || (Array.isArray(v) && v.length === 0));
};
/** Nodes whose showIf holds for the given answers, in form order. */
export function visibleNodes(form, answers) {
    return flatten(form).filter((f) => !f.node.showIf || evaluateCondition(f.node.showIf, answers));
}
/**
 * Computes where the conversation stands. Message nodes are shown in sequence;
 * the first visible answering node without a valid answer is the next question.
 */
export function flowState(form, answers) {
    const visible = visibleNodes(form, answers);
    const transcript = [];
    const pendingMessages = [];
    let next;
    for (const f of visible) {
        if (f.node.type === "message") {
            if (next)
                break;
            transcript.push({ kind: "system", node: f });
            pendingMessages.push(f);
            continue;
        }
        if (answered(answers, f.node.id)) {
            pendingMessages.length = 0;
            transcript.push({ kind: "system", node: f });
            transcript.push({
                kind: "answer",
                node: f,
                value: answers[f.node.id],
            });
            continue;
        }
        if (f.node.required || !(f.node.id in answers)) {
            next = f;
            transcript.push({ kind: "system", node: f });
            break;
        }
        // optional node explicitly skipped (answers[id] === "")
        transcript.push({ kind: "system", node: f });
    }
    const completed = next === undefined;
    const stepsWithQuestions = new Map();
    for (const f of visible)
        if (f.node.type !== "message")
            stepsWithQuestions.set(f.stepId, [...(stepsWithQuestions.get(f.stepId) ?? []), f]);
    const completedSteps = [...stepsWithQuestions.entries()]
        .filter(([, nodes]) => nodes.every((n) => answered(answers, n.node.id) || (!n.node.required && n.node.id in answers)))
        .map(([id]) => id);
    return {
        next,
        pendingMessages,
        completed,
        completedSteps,
        currentStepId: next?.stepId,
        transcript,
    };
}
/**
 * Applies an answer and drops answers whose showIf no longer holds (5.3 editing
 * rule). Returns the new answers and the ids that were invalidated.
 */
export function applyAnswer(form, answers, nodeId, value) {
    const nextAnswers = { ...answers, [nodeId]: value };
    const invalidated = [];
    // Iterate until stable: hiding one node can hide another that depended on it.
    let changed = true;
    while (changed) {
        changed = false;
        for (const f of flatten(form)) {
            if (!f.node.showIf || nextAnswers[f.node.id] === undefined)
                continue;
            if (!evaluateCondition(f.node.showIf, nextAnswers)) {
                delete nextAnswers[f.node.id];
                invalidated.push(f.node.id);
                changed = true;
            }
        }
    }
    return { answers: nextAnswers, invalidated };
}
/** Node ids whose visibility depends on the given node. */
export function dependents(form, nodeId) {
    return flatten(form)
        .filter((f) => f.node.showIf && conditionRefs(f.node.showIf).includes(nodeId))
        .map((f) => f.node.id);
}
/** True when the consent node has a valid accepted answer. Nothing is persisted before this (D4). */
export function consentAccepted(form, answers) {
    const consent = flatten(form).find((f) => f.node.type === "consent");
    if (!consent || consent.node.type !== "consent")
        return false;
    const v = answers[consent.node.id];
    if (typeof v !== "object" || v === null || Array.isArray(v))
        return false;
    return consent.node.items
        .filter((i) => i.required)
        .every((i) => v[i.key] === true);
}
//# sourceMappingURL=flow.js.map